import { Box, Button } from 'braid-design-system';
import queryString from 'query-string';
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import Routes from 'src/config/routes';
import { isFreemiumExperiment } from 'src/data/listing-products/products';
import { type ListingProduct, ListingProductCode } from 'src/generated/graphql';
import { fireHeapEvent } from 'src/utils/analytics';

interface Props {
  product: ListingProduct;
}

const fireSelectedPrivateSellerSelectedProductLoggedIn = (
  product: ListingProduct,
) => {
  let selectedProductName: string;
  // everything has to be renamed to appropriate codes for heap to sync with .NET's event
  // Refer to: https://github.com/SEEK-Jobs/sbiz-seek-business/blob/master/websites/SeekBusiness.BizSeeker/Views/Shared/PrivateSellerInfo/_packages3Choice.cshtml#L327
  // No need to define for the other bundled products as that is on checkout. These are only for the offerred base products on /sell/packages
  switch (product.productCode) {
    case ListingProductCode.SixAdPack:
      selectedProductName = 'sixmonthadpack';
      break;
    case ListingProductCode.ListUntilSold:
      selectedProductName = 'listuntilsold';
      break;
    case ListingProductCode.PayPerEnquiry:
      selectedProductName = 'payperenquiry';
      break;
    case ListingProductCode.Freemium:
      selectedProductName = 'freemium';
      break;
    case ListingProductCode.Accelerate:
      selectedProductName = 'accelerate';
      break;
    default:
      selectedProductName = product.description;
  }
  fireHeapEvent('privateSellerSelectedProductLoggedIn', {
    SelectedProductToken: product.productToken.toUpperCase(), // always send upper case as per .NET
    SelectedProductName: selectedProductName,
  });
};

const SelectButton = ({ product }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { listingId } = queryString.parseUrl(location.search).query;

  const handleSubmit = () => {
    fireSelectedPrivateSellerSelectedProductLoggedIn(product);

    if (product.productCode === ListingProductCode.PayPerEnquiry) {
      navigate(Routes.PAY_PER_ENQUIRY_CONTRACT);
      return;
    }

    if (product.productCode === ListingProductCode.Freemium) {
      navigate(Routes.FREEMIUM_CONTRACT);
      return;
    }

    const checkoutUrl = queryString.stringifyUrl({
      url: Routes.PAYMENT,
      query: {
        productToken: product.productToken,
        listingId: listingId ?? undefined,
      },
    });

    navigate(checkoutUrl);
  };

  return (
    <Box paddingX="small">
      <Button
        onClick={handleSubmit}
        data={{
          // This is used for Heap analytics
          htag: product.legacyProductCode,
        }}
      >
        {isFreemiumExperiment ? `Choose ${product.description}` : 'Select'}
      </Button>
    </Box>
  );
};

export default SelectButton;
